import styled from '@emotion/styled'

import { color, colors, theme } from '../../../Theme'

// ---------------------------------------------------------------------------
// Local palette & layout constants
// ---------------------------------------------------------------------------
// Exact hex/rgba values preserved from the original inline styles, grouped here so they read as
// intentional rather than magic and have a single place to change. Candidates to migrate into the
// shared theme once equivalent tokens exist.
export const TEXT_PRIMARY = '#333'
export const TITLE_DIMMED = '#9d9d9d'
const DESC_DIMMED = '#7f7f7f'
const BORDER_DIMMED = '#777'
const CARD_BORDER = '#ccc'
const REASONING_TEXT = '#555'
const SECTION_DIVIDER = 'rgba(0, 0, 0, 0.08)'
const PLACEHOLDER_BORDER = 'rgba(0, 0, 0, 0.12)'
const OPTION_HOVER_BG = 'rgba(36, 121, 179, 0.1)'
const FIELD_BG = '#ffffff'

// Delay before smooth-scrolling to a newly active section, letting layout settle first.
export const SCROLL_DELAY_MS = 150

export type OptionState = 'active' | 'selected' | 'chosen' | 'dimmed' | 'placeholder' | 'secondary'

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid ${SECTION_DIVIDER};
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const SectionQuestion = styled.span<{ muted?: boolean }>`
  ${theme.text('normal', 'decorative', 'bold')};
  color: ${colors.purple20};
  opacity: ${(p) => (p.muted ? 0.5 : 1)};
  padding-left: 4px;
`

export const SectionReasoning = styled.span`
  ${theme.text('mediumSmall', 'content')};
  color: ${REASONING_TEXT};
  padding-left: 4px;
`

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

// 8px vertical gap used between the matched-defect card and the action question.
export const Spacer = styled.div`
  height: 8px;
`

export const ScrollOptionButton = styled.button<{ optionState: OptionState }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  /* Selected/chosen get a 4px border; padding shrinks by 3px so the text doesn't jump */
  padding: ${(p) =>
    p.optionState === 'selected' || p.optionState === 'chosen' ? '7px 11px' : '10px 14px'};
  border-radius: ${theme.borderRadius.button};
  text-align: left;
  transition: border-color 0.15s;
  background: ${color('white')};
  border-style: solid;
  border-width: ${(p) =>
    p.optionState === 'selected' || p.optionState === 'chosen' ? '4px' : '1px'};
  border-color: ${(p) =>
    p.optionState === 'selected' || p.optionState === 'chosen'
      ? colors.purple20
      : p.optionState === 'dimmed' || p.optionState === 'secondary'
      ? BORDER_DIMMED
      : p.optionState === 'placeholder'
      ? PLACEHOLDER_BORDER
      : colors.purple20};
  opacity: ${(p) => (p.optionState === 'placeholder' ? 0.2 : 1)};
  cursor: ${(p) =>
    p.optionState === 'active' || p.optionState === 'secondary' || p.optionState === 'chosen'
      ? 'pointer'
      : 'default'};
  pointer-events: ${(p) =>
    p.optionState === 'active' || p.optionState === 'secondary' || p.optionState === 'chosen'
      ? 'auto'
      : 'none'};
  &:hover {
    background: ${(p) =>
      p.optionState === 'active' || p.optionState === 'secondary' || p.optionState === 'chosen'
        ? OPTION_HOVER_BG
        : undefined};
  }
`

export const OptionTitle = styled.span<{ dimmed?: boolean }>`
  ${theme.text('normal', 'decorative', 'bold')};
  color: ${(p) => (p.dimmed ? TITLE_DIMMED : colors.purple20)};
`

export const OptionDesc = styled.span<{ dimmed?: boolean }>`
  ${theme.text('mediumSmall', 'content')};
  color: ${(p) => (p.dimmed ? DESC_DIMMED : TEXT_PRIMARY)};
`

export const MatchedDefectCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: transparent;
  border: 1px solid ${CARD_BORDER};
  border-radius: ${theme.borderRadius.button};
`

export const FieldPair = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const FieldLabel = styled.span`
  ${theme.text('normal', 'content', 500)};
`

export const FieldValue = styled.span`
  ${theme.text('normal', 'content', 400)};
`

export const FinishSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`

export const AI_FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: FIELD_BG,
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: colors.purple20,
  },
} as const
